import React, {Fragment, useCallback, useContext} from "react";
import {connect} from "react-redux";
import {FormattedMessage} from "react-intl";
import {useDropzone} from "react-dropzone";

import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";

import LookAiPopupContext from "./LookAiPopupContext";
import {setAiUserFileBlobUrl, setLookAiImageOpened} from "../../aiActions";
import LookAIPopupDropZoneError from "./LookAIPopupDropZoneError";
import PRLoader from "../../../ui/prLoader";
import LookAILoadingBar from "../lookAIOverlays/LookAILoadingBar";
import {applyLookAiUserFile} from "./lookAIUserFile";


const LookAIPopupDropZone = (props) => {

  const {aiLoading, setAiUserFileBlobUrl, setLookAiImageOpened, aiResults, isMobile} = props;

  const {lookAiPopupContext, lookAiPopupDispatch} = useContext(LookAiPopupContext);
  const {userFileBlobUrl, showSourceChooser, qrPanelOpened} = lookAiPopupContext;

  const onDrop = useCallback(acceptedFiles => {
    if (!acceptedFiles || !acceptedFiles[0]) {
      return
    }
    applyLookAiUserFile(acceptedFiles[0], lookAiPopupDispatch, setAiUserFileBlobUrl);
    lookAiPopupDispatch({qrPanelOpened: false}); // a local upload replaces a pending mobile (QR) handoff
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookAiPopupDispatch]);

  const activeResult = aiResults.find(aiResult => aiResult.isActive);
  const activeResultHasError = activeResult && activeResult.conversionError;
  const activeResultHasImage = activeResult && activeResult.resultImageUrl;

  const dropZoneDisabled = aiLoading || activeResultHasError;

  // clicks are handled by the "Upload Image" half only (open()), drag and drop still works on the whole tile
  const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
    onDrop,
    disabled: dropZoneDisabled,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: {
      'image/*': [],
    }
  });

  const hasImage = userFileBlobUrl && !aiLoading && !activeResultHasError;
  // no photo yet, the user asked to start again, or a file is dragged over the tile - show "Snap / Upload"
  const showChooser = !userFileBlobUrl || showSourceChooser || isDragActive;
  const imageIsClickable = hasImage && !showChooser && !dropZoneDisabled;

  // with a photo (or a generated result) the first click returns the tile to the "Snap / Upload" choice
  const onImageClick = () => {
    lookAiPopupDispatch({showSourceChooser: true});
  };

  const onSnapClick = (event) => {
    event.stopPropagation();
    setLookAiImageOpened(false); // the QR panel takes the images panel place
    if (!qrPanelOpened) {
      lookAiPopupDispatch({qrPanelOpened: true});
    }
  };

  const onUploadClick = (event) => {
    event.stopPropagation();
    open();
  };

  return (
    <div
      {...getRootProps({onClick: imageIsClickable ? onImageClick : undefined})}
      style={hasImage ? {backgroundImage: `url(${userFileBlobUrl})`} : null}
      className={"overflow-hidden look-ai__sidebar-content relative look-ai__sidebar-content--dropzone u-rounded-05 u-pr-1 u-pl-1 bg-cover bg-center bg-no-repeat"
        + (imageIsClickable ? " cursor-pointer" : "")} >
      <input {...getInputProps()} />
      {aiLoading ? (
        <Fragment>
          <div
            className="absolute look-ai__dropzone-blur u-left-0 u-top-0 u-w-full u-h-full bg-cover bg-center bg-no-repeat u-rounded-05"
            style={{backgroundImage: `url(${userFileBlobUrl})`}}></div>
          <div className="flex flex--center flex--column u-h-full relative text-center u-w-full">
            <PRLoader/>
            <div className="look-ai__dropzone-text u-leading-1 bold u-w-full">
              <LookAILoadingBar/>
            </div>
          </div>
        </Fragment>
      ) : activeResultHasError ? (
        <LookAIPopupDropZoneError/>
      ) : showChooser ? (
        <div className={"absolute u-left-0 u-top-0 u-w-full u-h-full flex look-ai__source-chooser"
          + (hasImage ? " look-ai__source-chooser--with-image" : "")}>
          {isMobile ? null : (
            <Fragment>
              <button type="button"
                      className={"look-ai__source h-full flex-1 flex flex--column flex--center u-gap-025 u-p-05"
                        + (qrPanelOpened ? " look-ai__source--active" : "")}
                      onClick={onSnapClick}>
                <QrCode2Icon className="look-ai__source-icon"/>
                <span className="look-ai__title u-leading-1 bold">
                  <FormattedMessage id="look.ai.snap.title"/>
                </span>
                <span className="look-ai__text u-leading-1">
                  <FormattedMessage id="look.ai.snap.text"/>
                </span>
              </button>
              <div className="look-ai__source-divider"/>
            </Fragment>
          )}
          <button type="button"
                  className="look-ai__source h-full flex-1 flex flex--column flex--center u-gap-025 u-p-05"
                  onClick={onUploadClick}>
            <CreateNewFolderOutlinedIcon className="look-ai__source-icon"/>
            <span className="look-ai__title u-leading-1 bold">
              <FormattedMessage id="look.ai.sidebar.title"/>
            </span>
            <span className="look-ai__text u-leading-1">
              <FormattedMessage id={isDragActive
                ? "look.ai.panel.dropActive"
                : isMobile ? "look.ai.sidebar.mobile.text" : "look.ai.sidebar.text"}/>
            </span>
          </button>
        </div>
      ) : activeResultHasImage ? (
        <div className="flex flex--column flex--center u-w-full u-h-full">
          <div className="absolute u-left-1/2 nowrap u-p-1 u-rounded-05 u-top-1/2 look-ai__upload look-ai__upload--with-image">
            <div className="look-ai__title u-leading-1 bold flex flex--center u-gap-025">
              <CreateNewFolderOutlinedIcon/>
              <span><FormattedMessage id="look.ai.sidebar.title"/></span>
            </div>
            <span className="look-ai__text u-leading-1">
              <FormattedMessage id={isMobile ? "look.ai.sidebar.mobile.text" : "look.ai.sidebar.text"}/>
            </span>
          </div>
        </div>
      ) : null}
    </div>
  )
};

const mapStateToProps = (store) => {
  const {aiLoading, aiResults} = store.aiState;
  const {isMobile} = store.serverState;

  return {
    aiLoading,
    aiResults,
    isMobile,
  };
};

const mapDispatchToProps = {
  setAiUserFileBlobUrl,
  setLookAiImageOpened,
};

export default (
  connect(mapStateToProps, mapDispatchToProps)(LookAIPopupDropZone)
)
